<template>
  <div>
    <c-loader v-if="!inited" :active="!inited" :height="100" />

    <div v-show="inited">
      <div class="form-group field-form__type">
        <div v-if="!customFields.length" class="empty">Созданных полей пока нет</div>

        <c-select
          v-else
          v-model="original"
          type="select"
          floating-label="Расположение поля"
          :required-mark="true"
          :searchable="true"
          :items="customFields"
          :groups="customFieldsGroups"
          :required="isSubmitted && originalError"
          :required-message="isSubmitted && originalError"
        />
      </div>

      <c-loader v-if="original && !loaded" :active="!loaded" :height="100" />

      <custom-form
        ref="form"
        :inited="loaded"
        :is-copy="true"
        :form="form"
        :is-submitted="isSubmitted"
        :warning="showWarning"
        @submit="submit"
        @changeDictionary="changedDictionary = $event"
      />
    </div>
  </div>
</template>

<script>
import CLoader from "@/components/loader";
import CSelect from "@/components/controls2/Select";
import CustomForm from "./CustomForm.vue";

import { CUSTOM_TYPES } from "@/domains/profile/constants/custom-types";
import { CustomFormModel } from "../models/CustomFormModel";
import { ProfileFieldModel } from "@/domains/profile/models/ProfileFieldModel";
import { REQUIRED_ERROR } from "@/constants/errorFieldValues";

export default {
  components: {
    CLoader,
    CSelect,
    CustomForm,
  },
  props: ["profileId", "fields", "saveFieldData", "saveDictionary"],
  data() {
    return {
      inited: false,
      loaded: false,
      isSubmitted: false,
      form: CustomFormModel(),
      customFields: [],
      customFieldsGroups: [],
      original: null,
      originalData: null,
      changedDictionary: null,
    };
  },
  computed: {
    usedFields() {
      return this.fields.map((field) => field.parentId);
    },
    showWarning() {
      if (!this.original || !this.originalData) return false;
      const originalName = this.originalData.name;
      if (originalName !== this.form.data.name) return true;
      if (this.changedDictionary) return true;
      return false;
    },
    originalError() {
      return this.original ? null : REQUIRED_ERROR;
    },
    isValid() {
      return !this.originalError;
    },
  },
  watch: {
    original(v) {
      this.updateFieldData(v && v.value);
    },
  },

  created() {
    this.updateCustomFieldsList();
  },
  methods: {
    formatCustomFieldsList(data) {
      const added = [];

      const groups = [];
      const list = [];

      data.forEach((profile) => {
        const { profileId, profileName, types } = profile;
        if (!profileId) return;

        if (!types) return;

        const typesList = Array.isArray(types) ? types : Object.values(types);

        typesList.forEach((type) => {
          const { typeName, tabs } = type;

          const groupName = `${profileId}/${profileName}/${typeName}`;
          const formattedGroupName = `<b>${profileName}</b>/${typeName}`;

          groups.push({
            label: formattedGroupName,
            name: groupName,
          });

          tabs.forEach((tab) => {
            const { fields } = tab;

            fields.forEach((field) => {
              const { id: fieldId, name: fieldName, profile_field_id: fieldSettingsId } = field;

              if (added.includes(fieldId)) return;

              added.push(fieldId);

              const isUsed = this.usedFields.includes(`${fieldSettingsId}`);

              list.push({
                value: fieldId,
                resultLabel: `${formattedGroupName}/${fieldName}`,
                label: fieldName,
                formatLabel: `${fieldName} ${isUsed ? "<span class='used'>(добавлено)</span>" : ""}`,
                group: groupName,
                disabled: isUsed,
              });
            });
          });
        });
      });

      list.sort((a, b) => {
        if (a.group === b.group) return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
        return a.group.toLowerCase() > b.group.toLowerCase() ? 1 : -1;
      });

      return {
        fields: list,
        groups,
      };
    },
    async updateCustomFieldsList() {
      this.inited = false;
      const response = await this.$store.dispatch("profile/getCustomFields");

      const { fields, groups } = this.formatCustomFieldsList(response);

      this.customFieldsGroups = groups;
      this.customFields = fields;

      this.$nextTick(() => {
        this.inited = true;
      });
    },

    getFieldConfig(fieldType) {
      return CUSTOM_TYPES[fieldType];
    },
    async loadFieldData(fieldId) {
      const data = await this.$store.dispatch("profile/getField", fieldId);
      return ProfileFieldModel(data);
    },

    async updateFieldData(fieldId) {
      this.loaded = false;

      if (!fieldId) {
        this.originalData = null;
        this.form.reset();
        return;
      }

      const id = fieldId;

      const field = await this.loadFieldData(id);
      this.originalData = field;

      this.form.setData(field);

      const fieldConfig = this.getFieldConfig(field.fieldType);
      if (fieldConfig.isVariants || fieldConfig.isList) {
        await this.$store.dispatch("profile/getDictionary", { fieldId: field.parentId }).then((list) => {
          this.form.setVariants(list);
        });
      }

      this.$nextTick(() => {
        this.loaded = true;
      });
    },

    reset() {
      this.isSubmitted = false;
      this.original = null;
    },

    async submit() {
      this.isSubmitted = true;
      if (!this.isValid || !this.$refs.form.isValid) {
        return;
      }

      this.$emit("pending", true);

      const params = this.form.getParams();
      const { parentId } = this.originalData;
      params.profile_field_id = parentId;

      let fieldModel = null;
      const fieldRequest = this.saveFieldData(params).then((model) => {
        fieldModel = model;
        fieldModel.blocked = true;
        return model;
      });

      const type = this.form.data.fieldType;
      const fieldConfig = this.getFieldConfig(type.value);

      if (!fieldConfig.isVariants && !fieldConfig.isList) {
        await fieldRequest;
      } else if (!this.changedDictionary || this.changedDictionary !== type.value) {
        await fieldRequest;
      } else {
        const dictionary = this.form.getDictionary();
        const dictionaryRequest = this.saveDictionary({ dictionary, fieldId: parentId });
        await Promise.all([fieldRequest, dictionaryRequest]);
      }

      this.$emit("submitted", fieldModel);
      this.$emit("pending", false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.empty {
  color: $color-label;
}

.field-form__type {
  /deep/ {
    .c-select__group-label {
      color: black;
      font-weight: 400;
    }
  }
}

/deep/ {
  .used {
    font-size: 11px;
  }
}
</style>
